import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import tcircle from "@turf/circle";
import { Units, feature, featureCollection } from "@turf/helpers";
import { IS_INTERNAL_ENV } from "constants/app.constants";
import {
  HIGHLIGHTED_WELL_LAYER,
  SYNC_WELL_LAYER,
  WELL_LAYER
} from "constants/mapLayers.constants";
import { SyncWells, filterWellsFromMapExtent } from "store/features";
import { RootState } from "store/rootReducer";
import { useMapStore } from "stores/map";

import { waitForStyleToLoad } from "components/map/utils";
import { emptyFeatureCollection } from "components/map/utils/emptyFeatureCollection";
import useUserSettings from "components/user/hooks/useUserSettings";

import { useSyncWellHighlighting } from "../map/hooks/useSyncWellHighlighting";

export default function GoToSyncWells({ mapbox, onSync }) {
  const dispatch = useDispatch();
  const setMapState = useMapStore((s) => s.setMapState);
  const syncWells: SyncWells = useSelector((state: RootState) => state.app.syncWells);
  const { autoZoomSetting } = useUserSettings();

  // Track the active well operation
  const activeOperationRef = useRef(null);

  useSyncWellHighlighting(mapbox, HIGHLIGHTED_WELL_LAYER);

  useEffect(() => {
    if (!IS_INTERNAL_ENV || !mapbox) {
      return;
    }

    // Cancel any in-progress operations
    if (activeOperationRef.current) {
      activeOperationRef.current.cancelled = true;
    }

    // Create a new operation identifier
    const currentOperation = { cancelled: false, wellId: syncWells?.activeWells?.[0] };
    activeOperationRef.current = currentOperation;

    // Helper functions
    const updateHighlightedFeatures = (feats) => {
      if (!mapbox) {
        return;
      }
      const source = mapbox.getSource(HIGHLIGHTED_WELL_LAYER);
      if (source) source.setData(feats);
    };

    const removeHighlightedFeatures = () => {
      if (!mapbox) {
        return;
      }
      const source = mapbox.getSource(HIGHLIGHTED_WELL_LAYER);
      if (source) source.setData(emptyFeatureCollection);
    };

    const clearSyncWell = () => {
      const source = mapbox.getSource(SYNC_WELL_LAYER);
      if (source) source.setData(emptyFeatureCollection);
    };

    const cleanup = () => {
      mapbox.off("moveend");
    };

    // Only proceed if we have the necessary data
    if (syncWells?.centroid && syncWells?.showWellsWithin) {
      const lng = syncWells.centroid.coordinates[0];
      const lat = syncWells.centroid.coordinates[1];
      const center = syncWells.centroid.coordinates;
      const radius = localStorage.getItem("xMiles")
        ? parseFloat(localStorage.getItem("xMiles"))
        : syncWells.xMiles;

      // Only update map location if autoZoom is not enabled
      if (autoZoomSetting?.isAutoZoomEnabled) {
        setMapState({ lng, lat, zoom: 10 });
      }

      onSync && onSync(syncWells.isSync && syncWells.showWellsWithin);

      // Clear previous event listeners
      cleanup();

      if (syncWells.isSync) {
        const handleMoveEnd = () => {
          // Check if this operation has been cancelled
          if (currentOperation.cancelled) return;

          // Check if we're still working with the same well
          if (currentOperation.wellId !== syncWells.activeWells?.[0]) return;

          // Wait for map style to load fully
          waitForStyleToLoad(mapbox, 100).then(() => {
            // Double-check that operation hasn't been cancelled during waiting
            if (currentOperation.cancelled) return;

            // Paint the active well marker (only if highlighting is enabled)
            if (
              syncWells.activeWells?.length > 0 &&
              syncWells.highlightSyncedWell !== false
            ) {
              const wells = mapbox.querySourceFeatures(WELL_LAYER, {
                sourceLayer: "eva-wells",
                filter: ["==", ["get", "Uwi"], syncWells.activeWells[0]]
              });

              const source = mapbox.getSource(SYNC_WELL_LAYER);
              if (source && wells.length > 0) {
                const features = wells.map((feat) => feature(feat.geometry));
                const collection = featureCollection(features);
                source.setData(collection);
              }
            } else if (syncWells.highlightSyncedWell === false) {
              // Clear the sync well layer if highlighting is disabled
              const source = mapbox.getSource(SYNC_WELL_LAYER);
              if (source) {
                source.setData(emptyFeatureCollection);
              }
            }

            // Add the circle and filter wells
            if (syncWells.showWellsWithin) {
              const unit: Units = "miles";
              const options = {
                steps: 24,
                units: unit,
                properties: { color: "#51b5a0" }
              };

              const circle = tcircle(center, radius, options);
              updateHighlightedFeatures(circle);

              // Filter wells inside circle
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const geom: any = { ...circle.geometry };
              geom.source = "selection";
              geom.operator = "=";

              dispatch(filterWellsFromMapExtent(geom));
            }
          });
        };

        mapbox.once("moveend", handleMoveEnd);

        // Clear previous well data
        clearSyncWell();

        // Fly to the new location - this will trigger the moveend event
        // Skip flying if autoZoom is enabled (user wants manual control)
        if (autoZoomSetting?.isAutoZoomEnabled) {
          mapbox.flyTo({
            center: [lng, lat],
            zoom: 10,
            duration: 0,
            essential: true
          });
        } else {
          handleMoveEnd();
        }
      } else {
        // If sync is off, clear all highlights and wells
        removeHighlightedFeatures();
        clearSyncWell();
      }
    } else if (!syncWells?.showWellsWithin) {
      // When unchecking the checkbox, clear the map lock
      removeHighlightedFeatures();
      clearSyncWell();
      onSync && onSync(false);
    }

    // Clean up when component unmounts or dependencies change
    return cleanup;
  }, [dispatch, syncWells]);

  return <></>;
}
